import type {
  ShowMeTheCoinAction,
  ShowMeTheCoinState,
  ShowMeTheCoinViewerState,
} from "@cloudnest/show-me-the-coin-engine";
import {
  filterStateForViewer,
  validateAndReduceGameState,
} from "@cloudnest/show-me-the-coin-engine";
import { showMeTheCoinEventToAction } from "./event-adapter";
import type { ShowMeTheCoinReplayFixtureEvent } from "./replay-fixtures.generated";

/**
 * UI-free 쇼 미 더 코인 (show-me-the-coin) replay simulation. `init` seeds
 * canonical state; every other event is reconstructed via the event adapter and
 * folded through the engine reducer. Fails loudly on unsupported/rejected events.
 *
 * Hidden information: each snapshot carries the canonical `state` (for the recap
 * toggle) and an observer `viewer` projection where both players' coin
 * inventories are hidden and current submissions are reduced to count-only.
 */
export interface ShowMeTheCoinReplaySnapshot {
  readonly index: number;
  readonly event: ShowMeTheCoinReplayFixtureEvent;
  readonly action: ShowMeTheCoinAction | null;
  readonly state: ShowMeTheCoinState;
  readonly viewer: ShowMeTheCoinViewerState;
}

function requireState(
  state: ShowMeTheCoinState | null,
  eventType: string,
): ShowMeTheCoinState {
  if (state === null) throw new Error(`${eventType} before init`);
  return state;
}

function readInitState(payload: Record<string, unknown>): ShowMeTheCoinState {
  const candidate = (payload as { state?: unknown }).state;
  if (typeof candidate !== "object" || candidate === null) {
    throw new Error("show-me-the-coin init event is missing a state snapshot");
  }
  return candidate as ShowMeTheCoinState;
}

function snapshot(
  index: number,
  event: ShowMeTheCoinReplayFixtureEvent,
  action: ShowMeTheCoinAction | null,
  state: ShowMeTheCoinState,
): ShowMeTheCoinReplaySnapshot {
  return {
    index,
    event,
    action,
    state,
    viewer: filterStateForViewer(state, null),
  };
}

export function buildShowMeTheCoinReplaySnapshots(
  events: readonly ShowMeTheCoinReplayFixtureEvent[],
): readonly ShowMeTheCoinReplaySnapshot[] {
  const snapshots: ShowMeTheCoinReplaySnapshot[] = [];
  let state: ShowMeTheCoinState | null = null;

  for (const event of events) {
    if (event.eventType === "init") {
      state = readInitState(event.payload);
      snapshots.push(snapshot(snapshots.length, event, null, state));
      continue;
    }

    const current = requireState(state, event.eventType);
    const action = showMeTheCoinEventToAction(event.eventType, event.payload);
    const result = validateAndReduceGameState(current, action);
    if (!result.accepted) {
      throw new Error(
        `${event.eventType} (#${event.sequenceNumber}) rejected: ${
          result.error ?? "Validation failed"
        }`,
      );
    }
    state = result.state;
    snapshots.push(snapshot(snapshots.length, event, action, state));
  }

  if (state === null) {
    throw new Error("Cannot replay an empty show-me-the-coin event log");
  }
  return snapshots;
}
